import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Alert,
  CircularProgress,
  Collapse,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import {
  useTSQBOMRevListView,
  useTSMDeactivateBOMRev,
  useTSMReactivateBOMRev,
} from "../../hooks/useTSBOMs";
import { ListRequestFilter } from "../../types/common/commonTypes";
import { BOMRevFull, BomRevListView } from "../../types/bomTypes";
import { paginationDefaults } from "../../config/settingsDefaults";
import { useAuth } from "../../contexts/AuthContextWithQuery";

const FilterBox: React.FC<{
  finalSet: React.Dispatch<React.SetStateAction<string>>;
}> = ({ finalSet }) => {
  const [localVal, setLocalVal] = useState<string>("");
  return (
    <TextField
      label="Search"
      value={localVal}
      onChange={(e) => setLocalVal(e.target.value)}
      onBlur={(e) => finalSet(e.target.value)}
      placeholder="Search BOM by name or Item Number..."
      slotProps={{
        input: {
          startAdornment: (
            <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
          ),
        },
      }}
      sx={{ minWidth: 300 }}
    />
  );
};

export const PageBOMList: React.FC = () => {
  const navigate = useNavigate();

  const { user } = useAuth();

  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    page: 0,
    size: paginationDefaults.startingRowOption,
  });
  const [error, setError] = useState<string | null>(null);

  const [orderBy, setOrderBy] = useState<string>("bom_revisions.version_name");
  const [order, setOrder] = useState<"asc" | "desc">("asc");

  const [recTotal, setRecTotal] = useState<number>(0);
  const [foundBOMs, setFoundBOMs] = useState<BomRevListView[]>([]);
  const [shouldSearch, setShouldSearch] = useState<boolean>(true);
  const refShouldSearch = useRef(shouldSearch);

  useEffect(() => {
    refShouldSearch.current = shouldSearch;
  });

  const {
    data: bomsResponse,
    isLoading: bomsLoading,
    error: queryError,
    isPlaceholderData,
  } = useTSQBOMRevListView(
    {
      page: pagination.page,
      page_size: pagination.size,
      sort_by: `${orderBy} ${order.toLocaleUpperCase()}`,
      filter:
        searchTerm === "" || !shouldSearch
          ? null
          : `bom_revisions.version_name ILIKE '%${searchTerm}%' OR items.number ILIKE '%${searchTerm}%'`,
    },
    "bom_list",
    shouldSearch,
  );

  useEffect(() => {
    if (refShouldSearch.current) {
      if (bomsResponse?.items) {
        setFoundBOMs(bomsResponse.items);
        setRecTotal(bomsResponse.total);
        setShouldSearch(false);
      }
    }
  }, [bomsResponse]);

  const deactivateBOMRevMutation = useTSMDeactivateBOMRev();
  const reactivateBOMRevMutation = useTSMReactivateBOMRev();

  const handleDeactivate = async (id: string) => {
    if (!window.confirm("Are you sure you want to deactivate this BOM?")) {
      return;
    }

    try {
      await deactivateBOMRevMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to deactivate BOM:", err);
      setError(err.response?.data?.detail || "Failed to deactivate BOM");
    }
  };

  const handleReactivate = async (id: string) => {
    try {
      await reactivateBOMRevMutation.mutateAsync(id);
      setError(null);
    } catch (err: any) {
      console.error("Failed to reactivate BOM:", err);
      setError(err.response?.data?.detail || "Failed to reactivate BOM");
    }
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    setShouldSearch(true);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPagination((prev) => ({
      ...prev,
      size: parseInt(event.target.value, 10),
      page: 0,
    }));
    setShouldSearch(true);
  };

  const handleSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    setShouldSearch(true);
  };

  return (
    <Box>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4" component="h1">
          BOM Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate("/manufacturing/boms/form?mode=create")}
        >
          Create BOM
        </Button>
      </Stack>

      {(error || queryError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || queryError?.message || "An unknown error occurred"}
        </Alert>
      )}

      {shouldSearch && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Loading updated data...
        </Alert>
      )}

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FilterBox finalSet={setSearchTerm} />
          <Button
            variant="contained"
            color="info"
            onClick={() => {
              setShouldSearch(true);
            }}
          >
            Search BOMs
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <TablePagination
          component="div"
          count={recTotal}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                sortDirection={
                  orderBy === "bom_revisions.version_name" ? order : false
                }
              >
                <TableSortLabel
                  active={orderBy === "bom_revisions.version_name"}
                  direction={
                    orderBy === "bom_revisions.version_name" ? order : "asc"
                  }
                  onClick={() => handleSort("bom_revisions.version_name")}
                >
                  BOM Name
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={orderBy === "items.number" ? order : false}
              >
                <TableSortLabel
                  active={orderBy === "items.number"}
                  direction={orderBy === "items.number" ? order : "asc"}
                  onClick={() => handleSort("items.number")}
                >
                  Item Number
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  orderBy === "bom_revisions.effective_date" ? order : false
                }
              >
                <TableSortLabel
                  active={orderBy === "bom_revisions.effective_date"}
                  direction={
                    orderBy === "bom_revisions.effective_date" ? order : "asc"
                  }
                  onClick={() => handleSort("bom_revisions.effective_date")}
                >
                  Start Date
                </TableSortLabel>
              </TableCell>
              <TableCell
                sortDirection={
                  orderBy === "bom_revisions.expiration_date" ? order : false
                }
              >
                <TableSortLabel
                  active={orderBy === "bom_revisions.expiration_date"}
                  direction={
                    orderBy === "bom_revisions.expiration_date" ? order : "asc"
                  }
                  onClick={() => handleSort("bom_revisions.expiration_date")}
                >
                  End Date
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === "bom_revisions.is_record_active"}
                  direction={
                    orderBy === "bom_revisions.is_record_active" ? order : "asc"
                  }
                  onClick={() => handleSort("bom_revisions.is_record_active")}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shouldSearch ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : foundBOMs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary" py={4}>
                    No BOMs found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              foundBOMs.map((bom) => (
                <TableRow key={bom.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {bom.version_name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{bom.item}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {new Date(bom.effective_date).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {bom.expiration_date
                      ? new Date(bom.expiration_date).toLocaleDateString()
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {bom.expiration_date ? (
                      <Chip
                        label={
                          new Date(bom.expiration_date) < new Date()
                            ? "Inactive"
                            : "Active"
                        }
                        color={
                          new Date(bom.expiration_date) < new Date()
                            ? "error"
                            : "success"
                        }
                      />
                    ) : (
                      <Chip label="Active" color="success" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(
                              `/manufacturing/boms/form?mode=view&id=${bom.id}`,
                            )
                          }
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Edit BOM">
                        <IconButton
                          size="small"
                          onClick={() =>
                            navigate(
                              `/manufacturing/boms/form?mode=edit&id=${bom.id}`,
                            )
                          }
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {user?.is_superuser && (
                        <>
                          {!bom.is_record_active ? (
                            <Tooltip title="Reactivate BOM">
                              <IconButton
                                size="small"
                                onClick={() => handleReactivate(bom.id)}
                                color="success"
                                disabled={reactivateBOMRevMutation.isPending}
                              >
                                <RestoreIcon />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Deactivate BOM">
                              <IconButton
                                size="small"
                                onClick={() => handleDeactivate(bom.id)}
                                color="error"
                                disabled={deactivateBOMRevMutation.isPending}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={recTotal}
          page={pagination.page}
          onPageChange={handlePageChange}
          rowsPerPage={pagination.size}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={paginationDefaults.allRowOptions}
        />
      </TableContainer>
    </Box>
  );
};
